import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  BrandMark,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorText,
  Input,
  Label,
  PageTitle,
} from '@dataroom/shared';
import {
  AFFILIATE_KIT_CONTENTS,
  AFFILIATE_KIT_INSTRUCTION,
  AFFILIATE_KIT_PREVIEWS,
  AFFILIATE_KIT_ZIP_URL,
  AFFILIATE_VIDEOS,
  AFFILIATE_VIDEOS_INSTRUCTION,
  ENTRY_STATUS_LABELS,
  FIRM_STATUS_LABELS,
  formatDay,
  formatMoney,
  PROHIBITED_PROMOTION,
  VIOLATION_CONSEQUENCES,
  type AffiliateMe,
  type LedgerTotals,
} from '@/lib/affiliates';

/**
 * The affiliate portal (ADR-0042) — a separate principal from the admin app's
 * tenant users, with its own sign-in (an emailed 6-digit code; joining is the
 * same code plus a name) and its own session cookie. It never touches a tenant
 * endpoint. Signed in, it shows the referral link, the funnel, commissions by
 * status and how they are paid (ACH direct deposit; no bank details are kept here).
 */

const AFFILIATE_ME_KEY = ['affiliate-me'];

export function AffiliatePage() {
  const me = useQuery({
    queryKey: AFFILIATE_ME_KEY,
    queryFn: () => api.get<AffiliateMe>('/affiliates/me'),
    retry: false,
  });

  let body;
  if (me.isPending) {
    body = <p className="text-sm text-muted-foreground">Loading…</p>;
  } else if (me.data) {
    body = <AffiliateDashboard me={me.data} />;
  } else if (me.error instanceof ApiError && me.error.status === 401) {
    body = <AffiliateSignIn />;
  } else {
    body = <ErrorText error={me.error} />;
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <header className="border-b bg-background">
        <div className="mx-auto flex w-full max-w-container items-center px-4 py-3 md:px-6">
          <BrandMark appLabel="Affiliates" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-container px-4 py-6 md:px-6">{body}</main>
    </div>
  );
}

type SignInStep = 'overview' | 'email' | 'code' | 'name';
type SignInMode = 'join' | 'signin';

/**
 * Signed out: a short overview of the program first, with "Join the program"
 * and "Sign in". Only the chosen path asks for an email and the emailed code;
 * a new affiliate then gives a name (the API decides, by whether the address
 * already has an affiliate account).
 */
function AffiliateSignIn() {
  const qc = useQueryClient();
  const [step, setStep] = useState<SignInStep>('overview');
  const [mode, setMode] = useState<SignInMode>('join');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const address = email.trim();

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const requestCode = (e?: FormEvent) => {
    e?.preventDefault();
    void run(async () => {
      await api.post('/affiliates/code/request', { email: address });
      setCode('');
      setStep('code');
    });
  };

  const verify = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.post<{ ok?: boolean; nameRequired?: boolean }>(
        '/affiliates/code/verify',
        { email: address, code, ...(step === 'name' ? { name: name.trim() } : {}) },
      );
      if (res.nameRequired) {
        setStep('name');
        return;
      }
      await qc.invalidateQueries({ queryKey: AFFILIATE_ME_KEY });
    });
  };

  const start = (next: SignInMode) => {
    setMode(next);
    setError(null);
    setStep('email');
  };

  if (step === 'overview') {
    return (
      <div className="flex justify-center py-8">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle as="h1">ClosingVault affiliate program</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm">
              Refer firms to ClosingVault and earn 30% of what they pay in their first 12 months,
              20% in months 13–24 and 10% in months 25–36. Each commission becomes payable after a
              60-day hold, and we pay once a month by direct deposit (ACH) to a US bank account.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button className="flex-1" onClick={() => start('join')}>
                Join the program
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => start('signin')}>
                Sign in
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-8">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle as="h1">
            {mode === 'join' ? 'Join the affiliate program' : 'Affiliate sign in'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {step === 'email'
              ? 'We will email you a 6-digit code — no password needed.'
              : step === 'code'
                ? `Enter the 6-digit code we emailed to ${address}.`
                : mode === 'join'
                  ? 'Welcome! What name should we put on your affiliate account?'
                  : 'There is no affiliate account for this address yet. Add your name to join.'}
          </p>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form className="space-y-4" onSubmit={requestCode}>
              <div className="space-y-1.5">
                <Label htmlFor="affiliate-email">Email</Label>
                <Input
                  id="affiliate-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <ErrorText error={error} />
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? 'Sending…' : 'Email me a code'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setStep('overview');
                }}
              >
                Back
              </Button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={verify}>
              {step === 'code' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="affiliate-code">6-digit code</Label>
                  <Input
                    id="affiliate-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="affiliate-name">Your name</Label>
                  <Input
                    id="affiliate-name"
                    autoComplete="name"
                    maxLength={200}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
              )}
              <ErrorText error={error} />
              <Button
                type="submit"
                className="w-full"
                disabled={busy || code.length !== 6 || (step === 'name' && !name.trim())}
              >
                {busy ? 'Checking…' : step === 'name' ? 'Join the program' : 'Continue'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setStep('email');
                }}
              >
                Back
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AffiliateDashboard({ me }: { me: AffiliateMe }) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const logout = useMutation({
    mutationFn: () => api.post('/affiliates/logout'),
    onSuccess: () => qc.resetQueries({ queryKey: AFFILIATE_ME_KEY }),
  });

  const copy = () => {
    void navigator.clipboard?.writeText(me.referralLink).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PageTitle>Hi, {me.name}</PageTitle>
        <Button variant="ghost" onClick={() => logout.mutate()} disabled={logout.isPending}>
          Sign out
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your referral link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              readOnly
              value={me.referralLink}
              aria-label="Referral link"
              className="font-mono"
            />
            <Button type="button" variant="outline" onClick={copy}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Your link opens the ClosingVault free-trial sign-up. Firms that start a free trial
            within 90 days of their last click on your link are yours. You earn 30% of what they pay in their first 12 months, 20% in months 13–24 and 10% in
            months 25–36. Each commission becomes payable 60 days after the payment.
          </p>
          <div
            role="group"
            aria-label="Marketing kit"
            className="space-y-2 rounded-md border bg-muted/40 p-3"
          >
            <h3 className="text-sm font-semibold">Marketing kit</h3>
            <p className="text-sm">
              <a
                href={AFFILIATE_KIT_ZIP_URL}
                className="font-medium text-primary underline underline-offset-2"
              >
                Download marketing kit (.zip)
              </a>
            </p>
            <p className="text-sm text-muted-foreground">{AFFILIATE_KIT_INSTRUCTION}</p>
            <p className="text-sm text-muted-foreground">{AFFILIATE_KIT_CONTENTS}</p>
            <ul className="flex flex-wrap gap-2 text-sm">
              {AFFILIATE_KIT_PREVIEWS.map((p) => (
                <li key={p.audience}>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline underline-offset-2"
                  >
                    {p.label} image
                  </a>
                </li>
              ))}
            </ul>
            <h4 className="pt-1 text-sm font-semibold">Videos for your posts</h4>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {AFFILIATE_VIDEOS.map((v) => (
                <li key={v.name}>
                  <a
                    href={v.url}
                    download
                    className="font-medium text-primary underline underline-offset-2"
                  >
                    {v.label}
                  </a>
                </li>
              ))}
            </ul>
            <p className="text-sm text-muted-foreground">{AFFILIATE_VIDEOS_INSTRUCTION}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Clicks" value={me.funnel.clicks} />
        <Stat label="Free-trial signups" value={me.funnel.signups} />
        <Stat label="Paying firms" value={me.funnel.payingFirms} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Commissions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {me.totals.length === 0 ? (
            <p className="text-sm text-muted-foreground">No commissions yet.</p>
          ) : (
            me.totals.map((t) => (
              <div key={t.currency} className="space-y-3">
                <dl className="grid gap-3 text-sm sm:grid-cols-4">
                  <Money label="On hold (60 days)" cents={t.onHoldCents} currency={t.currency} />
                  <Money label="Payable" cents={t.payableCents} currency={t.currency} />
                  <Money label="Paid" cents={t.paidCents} currency={t.currency} />
                  <Money label="Adjustments" cents={t.adjustmentsCents} currency={t.currency} />
                  {t.excludedCents > 0 ? (
                    <p className="text-xs text-muted-foreground sm:col-span-4">
                      {formatMoney(t.excludedCents, t.currency)} not earned: self-referrals
                      aren&rsquo;t eligible — no commission on firms you own or work in.
                    </p>
                  ) : null}
                </dl>
                <PayoutStatus totals={t} me={me} />
              </div>
            ))
          )}
          {me.entries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 pr-3 font-medium">Firm</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Amount</th>
                    <th className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {me.entries.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="py-2 pr-3">{formatDay(e.occurredAt)}</td>
                      <td className="py-2 pr-3">{e.firmLabel}</td>
                      <td className="py-2 pr-3">
                        {e.kind === 'commission' ? 'Commission' : 'Adjustment'}
                      </td>
                      <td className="py-2 pr-3">{formatMoney(e.commissionCents, e.currency)}</td>
                      <td className="py-2">
                        {ENTRY_STATUS_LABELS[e.status]}
                        {e.status === 'on_hold' ? ` until ${formatDay(e.payableAt)}` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            For customer privacy, referred firms appear with a masked name. Only your commissions
            are shown — what each firm pays stays private.
          </p>
        </CardContent>
      </Card>

      <ReferredFirmsCard me={me} />
      <PayoutsCard me={me} />
      <HowYouGetPaidCard minPayoutCents={me.minPayoutCents} />
      <ProgramRulesCard />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="space-y-1 pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}

function Money({ label, cents, currency }: { label: string; cents: number; currency: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-lg font-semibold">{formatMoney(cents, currency)}</dd>
    </div>
  );
}

/** "$100.00" -> "$100" for round amounts, so the copy reads as the terms do. */
function formatWhole(cents: number, currency: string): string {
  return formatMoney(cents, currency).replace(/\.00$/, '');
}

/**
 * Under a currency's totals: what is payable and when it is paid, and the early
 * payout (a fee of $5 or 2%, whichever is more; owner decision 2026-10-24).
 */
function PayoutStatus({ totals: t, me }: { totals: LedgerTotals; me: AffiliateMe }) {
  const qc = useQueryClient();
  const quote = me.earlyPayoutQuotes.find((q) => q.currency === t.currency);
  const open = me.earlyPayoutRequest?.currency === t.currency ? me.earlyPayoutRequest : null;
  const request = useMutation({
    mutationFn: () => api.post('/affiliates/early-payout', { currency: t.currency }),
    onSuccess: () => qc.invalidateQueries({ queryKey: AFFILIATE_ME_KEY }),
  });
  if (t.payableCents <= 0 && !open) return null;
  const min = formatWhole(me.minPayoutCents, t.currency);
  return (
    <div className="space-y-2 rounded-md border bg-muted/40 p-3 text-sm">
      {t.payableCents > 0 ? (
        <p>
          Payable: <strong>{formatMoney(t.payableCents, t.currency)}</strong> —{' '}
          {t.payableCents >= me.minPayoutCents
            ? 'paid in the next monthly payout.'
            : `paid once it reaches ${min} (or request an early payout).`}
        </p>
      ) : null}
      {open ? (
        <p>
          Early payout requested on {formatDay(open.requestedAt)}:{' '}
          {formatMoney(open.grossCents, open.currency)} payable, a{' '}
          {formatMoney(open.feeCents, open.currency)} fee, so you&rsquo;ll receive{' '}
          <strong>{formatMoney(open.netCents, open.currency)}</strong>. We&rsquo;ll pay it in our
          next payout run.
        </p>
      ) : (
        <>
          <p>
            Need it sooner? Request an early payout now for a fee of $5 or 2%, whichever is more.
          </p>
          {quote ? (
            <>
              <p className="text-muted-foreground">
                On {formatMoney(quote.grossCents, quote.currency)} payable the fee is{' '}
                {formatMoney(quote.feeCents, quote.currency)}, so you&rsquo;d receive{' '}
                {formatMoney(quote.netCents, quote.currency)}. Commissions still on hold stay on
                hold.
              </p>
              <Button
                type="button"
                variant="outline"
                disabled={request.isPending}
                onClick={() => request.mutate()}
              >
                {request.isPending ? 'Requesting…' : 'Request early payout'}
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground">
              Your payable balance is too small for an early payout: the fee would leave nothing.
            </p>
          )}
          <ErrorText error={request.error} />
        </>
      )}
    </div>
  );
}

function ReferredFirmsCard({ me }: { me: AffiliateMe }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Referred firms</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {me.referredFirms.length === 0 ? (
          <p className="text-sm text-muted-foreground">No firms yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Firm</th>
                  <th className="py-2 pr-3 font-medium">Signed up</th>
                  <th className="py-2 pr-3 font-medium">Plan</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">Earned to date</th>
                </tr>
              </thead>
              <tbody>
                {me.referredFirms.map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-2 pr-3 font-medium">{f.name}</td>
                    <td className="py-2 pr-3">{formatDay(f.signedUpAt)}</td>
                    <td className="py-2 pr-3">{f.plan ?? '—'}</td>
                    <td className="py-2 pr-3">{FIRM_STATUS_LABELS[f.status]}</td>
                    <td className="py-2">
                      {f.earnings.length === 0
                        ? '—'
                        : f.earnings.map((e) => formatMoney(e.cents, e.currency)).join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          For customer privacy, referred firms appear with a masked name.
        </p>
      </CardContent>
    </Card>
  );
}

function PayoutsCard({ me }: { me: AffiliateMe }) {
  if (me.payouts.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Payouts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 pr-3 font-medium">Paid on</th>
                <th className="py-2 pr-3 font-medium">Gross</th>
                <th className="py-2 pr-3 font-medium">Fee</th>
                <th className="py-2 font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {me.payouts.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="py-2 pr-3">{formatDay(p.paidAt)}</td>
                  <td className="py-2 pr-3">{formatMoney(p.grossCents, p.currency)}</td>
                  <td className="py-2 pr-3">{formatMoney(p.feeCents, p.currency)}</td>
                  <td className="py-2 font-medium">{formatMoney(p.netCents, p.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function HowYouGetPaidCard({ minPayoutCents }: { minPayoutCents: number }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How you get paid</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">
          We pay by direct deposit (ACH) to a US bank account once a month. When you&rsquo;re first
          owed a payout, we&rsquo;ll contact you to collect your bank details securely.
        </p>
        <p className="text-sm text-muted-foreground">
          A monthly payout happens once your payable balance reaches{' '}
          {formatWhole(minPayoutCents, 'usd')}. Below that, it rolls forward every month and never
          expires. You can also request an early payout for a fee of $5 or 2%, whichever is more; it
          never shortens the 60-day hold.
        </p>
      </CardContent>
    </Card>
  );
}

function ProgramRulesCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>What isn&rsquo;t allowed</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {PROHIBITED_PROMOTION.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground">{VIOLATION_CONSEQUENCES}</p>
      </CardContent>
    </Card>
  );
}
